<template>
  <el-table :data="data" @row-click="$emit('edit', $event)">
    <el-table-column prop="name" label="Nom" min-width="240"></el-table-column>
    <el-table-column prop="type" label="Type" align="center" width="180">
      <template slot-scope="scope">
        <span class="badge badge-info">{{ scope.row.type }}</span>
      </template>
    </el-table-column>
    <template v-if="metadata.length > 0">
      <el-table-column
        v-for="item in metadata"
        :key="`metadata-col-${item.key}`"
        :prop="`metadata.${item.key}`"
        :label="item.label"
        align="center"
        width="150"
      ></el-table-column>
    </template>
    <el-table-column
      prop="createdAt"
      label="Créé le"
      align="center"
      width="150"
    >
      <template slot-scope="scope">
        {{ formatDate(scope.row.createdAt) }}
      </template>
    </el-table-column>
    <el-table-column prop="published" label="Publié" align="center" width="190">
      <template slot-scope="scope">
        <DatePeriodStatusBadge
          v-if="
            scope.row.publishedPeriod &&
            (scope.row.publishedPeriod.start || scope.row.publishedPeriod.end)
          "
          :start="scope.row.publishedPeriod.start"
          :end="scope.row.publishedPeriod.end"
        />
        <template v-else>
          <label
            v-if="hasWritePermission"
            @click.stop=""
            class="switch switch-sm switch-text switch-info"
          >
            <input
              type="checkbox"
              class="switch-input"
              v-model="scope.row.published"
              @change.stop="togglePublished(scope.row)"
            />
            <span class="switch-label" data-on="Oui" data-off="Non"></span>
            <span class="switch-handle"></span>
          </label>
          <span v-else>
            <i
              :class="`fa ${
                scope.row.published
                  ? 'fa-check text-success'
                  : 'fa-times text-danger'
              }`"
            ></i>
          </span>
        </template>
      </template>
    </el-table-column>
    <el-table-column
      v-if="canBeStatic"
      prop="isStatic"
      label="Fixe"
      align="center"
      width="100"
    >
      <template slot-scope="scope">
        <i v-if="scope.row.isStatic" class="fa fa-check text-success"></i>
      </template>
    </el-table-column>
    <el-table-column
      v-if="showUserFiltersColumn"
      prop="userFilters"
      label="Filtres coachs"
      align="center"
      width="140"
    >
      <template slot-scope="scope">
        <div
          v-if="scope.row.userFilters"
          class="d-flex flex-wrap justify-content-center align-items-center"
          style="gap: 0.25rem"
        >
          <div
            v-for="[key, value] in Object.entries(scope.row.userFilters).filter(
              ([k, v]) => {
                if (Array.isArray(v)) {
                  return v.length > 0;
                }

                return !!v;
              },
            )"
            :key="`${scope.row.id}-user-filters-${key}`"
            class="badge badge-info"
          >
            {{ key }} {{ Array.isArray(value) ? value.length : 1 }}
          </div>
        </div>
      </template>
    </el-table-column>
    <template v-if="canBeSent">
      <el-table-column
        prop="lastSentAt"
        label="Envoi"
        align="center"
        width="190"
      >
        <template slot-scope="scope">
          <button
            v-if="hasWritePermission"
            type="button"
            class="btn btn-sm btn-primary"
            @click.stop="$emit('send', scope.row)"
          >
            Envoyer <i class="fa fa-paper-plane-o ml-05"></i>
          </button>
          <div v-if="scope.row.lastSentAt" class="text-muted">
            Dernier envoi: {{ formatDate(scope.row.lastSentAt) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="autoSendAt"
        label="Programmer"
        align="center"
        width="190"
      >
        <template slot-scope="scope">
          <template v-if="scope.row.autoSendAt">
            <span>
              {{ formatDateTime(scope.row.autoSendAt) }}
            </span>
            <i
              v-if="scope.row.autoSendNotification"
              class="fa fa-bell-o text-success"
            ></i>
            <div v-if="hasWritePermission">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary ml-05"
                @click.stop="$emit('schedule', scope.row)"
              >
                <i class="fa fa-edit"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger ml-05"
                @click.stop="deleteAutoSend(scope.row)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </template>
          <button
            v-else-if="hasWritePermission"
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click.stop="$emit('schedule', scope.row)"
          >
            Programmer <i class="fa fa-clock-o ml-05"></i>
          </button>
          <span v-else class="text-muted">Non programmé</span>
        </template></el-table-column
      ><el-table-column
        prop="nbSent"
        label="Envoyés"
        align="center"
        width="100"
      ></el-table-column
      ><el-table-column
        prop="nbViewed"
        label="Vus"
        align="center"
        width="100"
      ></el-table-column>
    </template>
    <el-table-column v-if="hasWritePermission" width="100" align="right">
      <template slot-scope="scope">
        <button
          class="btn btn-primary btn-sm mr-05"
          @click.stop="$emit('edit', scope.row)"
        >
          <i class="fa fa-edit"></i>
        </button>
        <button
          class="btn btn-danger btn-sm"
          @click.stop="$emit('delete', scope.row)"
        >
          <i class="fa fa-trash-o"></i>
        </button>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import DatePeriodStatusBadge from "@/components/stats/DatePeriodStatusBadge";

export default {
  components: {
    DatePeriodStatusBadge,
  },

  props: {
    data: {
      type: Array,
      default: () => [],
    },

    type: {
      type: String,
      default: undefined,
    },

    writePermission: {
      type: String,
      default: null,
    },

    canBeSent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    hasWritePermission() {
      return (
        this.writePermission &&
        this.hasPermission(this.$store.state.user, this.writePermission)
      );
    },

    metadata() {
      switch (this.type) {
        case "MAJ_COACH_APP":
        case "TUTO_COACH_APP":
          return [{ label: "Numéro de version", key: "app_version" }];
        default:
          return [];
      }
    },

    showUserFiltersColumn() {
      return this.data.some(
        (item) => item.userFilters && Object.keys(item.userFilters).length > 0,
      );
    },

    canBeStatic() {
      return this.type === "CLIENT_APP_NEWS";
    },
  },

  methods: {
    async togglePublished(item) {
      try {
        await this.$api.patch(`/dynamic-contents/${item.id}/published`, {
          published: item.published,
        });
      } catch (e) {
        item.published = !item.published;

        this.$errorToast("Publication impossible");
      }
    },

    deleteAutoSend(item) {
      if (!item.autoSendAt) {
        return;
      }

      this.$confirm({
        message: `Êtes-vous sûr.e de vouloir supprimer la programmation de l'envoi pour "${
          item.name
        }" le ${this.formatDateTime(item.autoSendAt)} ?`,
        onConfirm: async () => {
          try {
            await this.$api.patch(`/dynamic-contents/${item.id}/auto-send`, {
              autoSendAt: null,
            });

            item.autoSendAt = null;
            item.autoSendNotification = false;
          } catch (e) {
            this.$errorToast("Impossible de supprimer la programmation");
          }
        },
      });
    },
  },
};
</script>
